/**
 * USSD presets for Uzbek mobile operators.
 * Last verified: 2026-08-16.
 * Sources: uzmobi.uz, mobi.uz, humans.uz, perfectum.uz, b2b.beeline.uz, ucell.uz, oq.uz.
 * Codes may change — first preset per operator must stay the "know number" command for auto-ussd.
 * Perfectum: panel presets only (`skipAutoUssd`); no reliable free USSD number without Personal Cabinet.
 */

export type UssdPresetCategory =
	| 'number'
	| 'balance'
	| 'limits'
	| 'tariff'
	| 'services'
	| 'packages'
	| 'other';

export type UssdPreset = {
	command: string;
	label: string;
	category?: UssdPresetCategory;
	/** USSD accepts request but MSISDN arrives via SMS (Beeline *303#). */
	asyncDelivery?: 'sms';
};

export type UssdMccMnc = { mcc: number; mnc: number };

export type UssdOperatorPresets = {
	matchKeys: string[];
	presets: UssdPreset[];
	/** MCC/MNC pairs (UZ 434/xx). MNC may be 4 or 5 on wire — normalized on match. */
	matchMccMnc?: UssdMccMnc[];
	/** Panel presets remain; auto-ussd will not send USSD for this operator. */
	skipAutoUssd?: boolean;
};

const UZ_MCC = 434;

const NUMBER_LABEL = 'Узнать свой номер';

const UZTELECOM_UZMOBILE_PRESETS: UssdPreset[] = [
	{ command: '*100*4#', label: NUMBER_LABEL, category: 'number' },
	{ command: '*100#', label: 'Управление счётом / баланс', category: 'balance' },
	{ command: '*100*2#', label: 'Остаток минут, трафика и SMS', category: 'limits' },
	{ command: '*107#', label: 'Баланс и лимиты (подробно)', category: 'limits' },
	{ command: '*100*7#', label: 'Текущий тарифный план', category: 'tariff' },
	{ command: '*100*5#', label: 'Номера на имени абонента', category: 'number' },
	{ command: '*100*3#', label: 'Курс доллара', category: 'other' },
	{ command: '*111*2*7*1#', label: 'Активация LTE (4G)', category: 'services' },
	{ command: '*111*2*7*2#', label: 'Деактивация LTE (4G)', category: 'services' },
	{ command: '*545#', label: 'Обмен минут ↔ МБ', category: 'services' },
	{ command: '*555#', label: 'RESTART (перезагрузка тарифа)', category: 'tariff' },
	{ command: '*43#', label: 'Активация ожидания вызова', category: 'services' },
	{ command: '*#67#', label: 'Проверка переадресации', category: 'services' },
	{ command: '*199#', label: 'Отключение инфо-развлекательных услуг', category: 'services' },
];

const BEELINE_PRESETS: UssdPreset[] = [
	{ command: '*148#', label: 'Узнать номер (альтернатива)', category: 'number' },
	{ command: '*1001#', label: 'Узнать номер (альтернатива 2)', category: 'number' },
	{ command: '*303#', label: NUMBER_LABEL, category: 'number', asyncDelivery: 'sms' },
	{ command: '*102#', label: 'Баланс счёта', category: 'balance' },
	{ command: '*103#', label: 'Остаток интернет-трафика (МБ)', category: 'limits' },
	{ command: '*106#', label: 'Остаток минут', category: 'limits' },
	{ command: '*105#', label: 'Остаток SMS', category: 'limits' },
	{ command: '*108#', label: 'Остатки по тарифу', category: 'limits' },
	{ command: '*107#', label: 'Beeline Club: минуты и SMS', category: 'limits' },
	{ command: '*117#', label: 'Beeline Club: бонусный баланс', category: 'balance' },
	{ command: '*110*05#', label: 'Текущий тариф (prepaid)', category: 'tariff' },
	{ command: '*110*005#', label: 'Текущий тариф (postpaid, с 24.12.2025)', category: 'tariff' },
	{ command: '*110*09#', label: 'Подключённые услуги (prepaid)', category: 'tariff' },
	{ command: '*110*009#', label: 'Подключённые услуги (postpaid, с 24.12.2025)', category: 'tariff' },
	{ command: '*110*00#', label: 'Курс доллара', category: 'other' },
	{ command: '*111#', label: 'Меню услуг', category: 'services' },
	{ command: '*141#', label: 'Доверительный платёж (кредит)', category: 'services' },
	{ command: '*110#', label: 'Подключение интернет-пакетов', category: 'packages' },
	{ command: '*1*1#', label: 'Интернет-пакет 1 GB / 30 дней', category: 'packages' },
	{ command: '*110*575#', label: 'YouTube 100 GB / 30 дней', category: 'packages' },
	{ command: '*120#', label: 'Остаток YouTube-пакета', category: 'limits' },
	{ command: '*0*10#', label: 'Тариф MULTI 1', category: 'packages' },
];

const UCELL_PRESETS: UssdPreset[] = [
	{ command: '*450#', label: NUMBER_LABEL, category: 'number' },
	{ command: '*100#', label: 'Баланс и лимиты', category: 'balance' },
	{ command: '*102#', label: 'Остаток лимитов', category: 'limits' },
	{ command: '*103#', label: 'Остаток лимитов (альт.)', category: 'limits' },
	{ command: '*104#', label: 'Остаток лимитов (альт. 2)', category: 'limits' },
	{ command: '*444#', label: 'USSD-меню (все услуги)', category: 'services' },
	{ command: '*111#', label: 'Меню услуг', category: 'services' },
	{ command: '*350#', label: 'Отключение платных услуг', category: 'services' },
	{ command: '*147#', label: 'SMS-пакеты', category: 'packages' },
	{ command: '*322#', label: 'SMS автоответ', category: 'services' },
	{ command: '*323#', label: 'Переадресация SMS', category: 'services' },
	{ command: '*233#', label: 'Одноразовый пароль (онлайн-покупки)', category: 'services' },
	{ command: '*220*2*2#', label: 'Язык системных сообщений (EN)', category: 'other' },
	{ command: '*555*3*1*1*1#', label: 'Интернет 100 MB / 31 день', category: 'packages' },
	{ command: '*555*3*2*1*1#', label: 'Интернет 300 MB / 31 день', category: 'packages' },
	{ command: '*555*3*3*1*1#', label: 'Интернет 600 MB / 31 день', category: 'packages' },
	{ command: '*555*3*4*1*1#', label: 'Интернет 1.5 GB / 31 день', category: 'packages' },
	{ command: '*555*3*5*1*1#', label: 'Интернет 3.5 GB / 31 день', category: 'packages' },
	{ command: '*555*3*6*1*1#', label: 'Интернет 5 GB / 31 день', category: 'packages' },
	{ command: '*555*3*7*1*1#', label: 'Интернет 8.5 GB / 31 день', category: 'packages' },
	{ command: '*555*3*8*1*1#', label: 'Интернет 12 GB / 31 день', category: 'packages' },
	{ command: '*555*3*9*1*1#', label: 'Интернет 16 GB / 31 день', category: 'packages' },
	{ command: '*555*3*11*1*1#', label: 'Интернет 32 GB / 31 день', category: 'packages' },
	{ command: '*555*3*12*1*1#', label: 'Интернет 64 GB / 31 день', category: 'packages' },
	{ command: '*555*3*13*1*1#', label: 'Интернет 128 GB / 31 день', category: 'packages' },
	{ command: '*555*3*10*1#', label: 'Отключение месячных интернет-пакетов', category: 'services' },
];

const MOBIUZ_PRESETS: UssdPreset[] = [
	{ command: '*150#', label: NUMBER_LABEL, category: 'number' },
	{ command: '*151#', label: 'Номера, зарегистрированные на абонента', category: 'number' },
	{ command: '*100#', label: 'Баланс, тариф и остатки', category: 'balance' },
	{ command: '*111#', label: 'Мобильный портал', category: 'services' },
	{ command: '*111*025#', label: 'Мои расходы за месяц', category: 'balance' },
	{ command: '*111*0021#', label: 'Справочник полезных команд', category: 'other' },
	{ command: '*111*1*1*1#', label: 'Баланс (узбекский)', category: 'balance' },
	{ command: '*111*1*1*2#', label: 'Баланс (русский)', category: 'balance' },
	{ command: '*111*1*1*3#', label: 'Баланс (английский)', category: 'balance' },
	{ command: '*111*0*1#', label: 'Язык по умолчанию (узбекский)', category: 'other' },
	{ command: '*111*0*2#', label: 'Язык по умолчанию (русский)', category: 'other' },
	{ command: '*111*0*3#', label: 'Язык по умолчанию (английский)', category: 'other' },
	{ command: '*111*015#', label: 'Переадресация вызовов', category: 'services' },
	{ command: '*111*0061#', label: 'Переадресация SMS', category: 'services' },
];

const HUMANS_PRESETS: UssdPreset[] = [
	{ command: '*664579#', label: NUMBER_LABEL, category: 'number' },
	{ command: '*110#', label: 'Баланс', category: 'balance' },
	{ command: '*107#', label: 'Остаток минут и ГБ', category: 'limits' },
	{ command: '*880#', label: 'Пополнение баланса', category: 'services' },
	{ command: '*100#', label: 'Добавить 100 MB (0.1 GB)', category: 'packages' },
	{ command: '*117#', label: 'Добавить минуты и ГБ', category: 'packages' },
	{ command: '*123#', label: 'SuperVIP 30 дней', category: 'packages' },
	{ command: '*104#', label: 'SuperVIP до конца пакета', category: 'packages' },
	{ command: '*105#', label: 'SuperVIP 72 часа', category: 'packages' },
	{ command: '*106#', label: 'SuperVIP 24 часа', category: 'packages' },
	{ command: '*777#', label: 'Безлимит минут + 7 GB', category: 'packages' },
	{ command: '*121#', label: 'Пакет 2 GB или 7 GB', category: 'packages' },
	{ command: '*118#', label: 'Запланировать Super0+0', category: 'tariff' },
	{ command: '*115#', label: 'Пакет 26 GB / 40 GB / безлимит минут', category: 'packages' },
	{ command: '*114#', label: 'Пакет 26 GB / 40 GB / безлимит минут', category: 'packages' },
	{ command: '*111#', label: 'Безлимит Telegram, Instagram и др.', category: 'packages' },
	{ command: '*116#', label: 'Автосписание с привязанной карты', category: 'services' },
	{ command: '*113#', label: 'Скидка на услуги (оплата кэшбэком)', category: 'services' },
];

const PERFECTUM_PRESETS: UssdPreset[] = [
	{ command: '*9*9#', label: 'Мои номера (требует Personal Cabinet)', category: 'number' },
	{ command: '*9*1#', label: 'Текущий баланс', category: 'balance' },
	{ command: '*100#', label: 'Баланс (альтернатива)', category: 'balance' },
	{ command: '*9*7#', label: 'Текущий тарифный план', category: 'tariff' },
	{ command: '*9*8#', label: 'Число номеров на договоре', category: 'number' },
	{ command: '*100*1#', label: 'Тарифный план (альтернатива)', category: 'tariff' },
	{ command: '*9*0#', label: 'Информация об услугах', category: 'services' },
	{ command: '*9*3#', label: 'Последний звонок', category: 'other' },
	{ command: '*9*4#', label: 'Последний платёж (цена и дата)', category: 'other' },
	{ command: '*9*5#', label: 'Начисленный платёж', category: 'other' },
	{ command: '*9*6#', label: 'Остаток льготного лимита', category: 'limits' },
	{ command: '*7*1*100#', label: 'Пакет 100 минут', category: 'packages' },
	{ command: '*7*1*200#', label: 'Пакет 200 минут', category: 'packages' },
];

const OQ_PRESETS: UssdPreset[] = [
	{ command: '*148#', label: NUMBER_LABEL, category: 'number' },
	{ command: '*100#', label: 'Баланс и пакеты', category: 'balance' },
	{ command: '*106#', label: 'Остаток минут в роуминг-пакете', category: 'limits' },
	{ command: '*112#', label: 'Остаток МБ в роуминг-пакете', category: 'limits' },
];

export const USSD_PRESET_CATEGORY_ORDER: UssdPresetCategory[] = [
	'number',
	'balance',
	'limits',
	'tariff',
	'services',
	'packages',
	'other',
];

export const USSD_PRESET_CATEGORY_LABELS: Record<UssdPresetCategory, string> = {
	number: 'Номер',
	balance: 'Баланс',
	limits: 'Остатки',
	tariff: 'Тариф',
	services: 'Услуги',
	packages: 'Пакеты',
	other: 'Прочее',
};

export const USSD_OPERATOR_PRESETS: UssdOperatorPresets[] = [
	{
		matchKeys: ['uztelecom', 'uzonline'],
		matchMccMnc: [{ mcc: UZ_MCC, mnc: 3 }, { mcc: UZ_MCC, mnc: 8 }],
		presets: UZTELECOM_UZMOBILE_PRESETS,
	},
	{
		matchKeys: ['uzmobile'],
		matchMccMnc: [{ mcc: UZ_MCC, mnc: 8 }],
		presets: UZTELECOM_UZMOBILE_PRESETS,
	},
	{
		matchKeys: ['beeline uz', 'beeline', 'unitel'],
		matchMccMnc: [{ mcc: UZ_MCC, mnc: 4 }],
		presets: BEELINE_PRESETS,
	},
	{
		matchKeys: ['ucell', 'coscom'],
		matchMccMnc: [{ mcc: UZ_MCC, mnc: 5 }],
		presets: UCELL_PRESETS,
	},
	{
		matchKeys: ['humans'],
		matchMccMnc: [{ mcc: UZ_MCC, mnc: 10 }],
		presets: HUMANS_PRESETS,
	},
	{
		matchKeys: ['mobiuz', 'ums'],
		matchMccMnc: [{ mcc: UZ_MCC, mnc: 7 }],
		presets: MOBIUZ_PRESETS,
	},
	{
		matchKeys: ['perfectum'],
		matchMccMnc: [{ mcc: UZ_MCC, mnc: 6 }],
		presets: PERFECTUM_PRESETS,
		skipAutoUssd: true,
	},
	{
		matchKeys: ['oq'],
		matchMccMnc: [{ mcc: UZ_MCC, mnc: 9 }],
		presets: OQ_PRESETS,
	},
	{
		matchKeys: ['vodafone ua'],
		presets: [{ command: '*161#', label: NUMBER_LABEL, category: 'number' }],
	},
	{
		matchKeys: ['kyivstar'],
		presets: [{ command: '*161#', label: NUMBER_LABEL, category: 'number' }],
	},
	{
		matchKeys: ['lifecell'],
		presets: [{ command: '*161#', label: NUMBER_LABEL, category: 'number' }],
	},
];

type KeyedOperatorEntry = { matchKey: string; operator: UssdOperatorPresets };

function getKeyedOperatorEntries(): KeyedOperatorEntry[] {
	return USSD_OPERATOR_PRESETS.flatMap(operator =>
		operator.matchKeys.map(matchKey => ({ matchKey, operator })),
	).sort((a, b) => b.matchKey.length - a.matchKey.length);
}

function normalizeMnc(mnc: number): string {
	if (mnc >= 100) return String(mnc);
	return String(mnc).padStart(2, '0');
}

function mccMncMatches(entry: UssdMccMnc, mcc: number, mnc: number): boolean {
	if (entry.mcc !== mcc) return false;
	const normalized = normalizeMnc(mnc);
	const entryNormalized = normalizeMnc(entry.mnc);
	return normalized === entryNormalized || String(mnc) === String(entry.mnc);
}

export function resolveUssdOperatorEntryByMccMnc(mcc: number | null | undefined, mnc: number | null | undefined): UssdOperatorPresets | null {
	if (mcc == null || mnc == null) return null;
	for (const operator of USSD_OPERATOR_PRESETS) {
		if (!operator.matchMccMnc?.length) continue;
		if (operator.matchMccMnc.some(entry => mccMncMatches(entry, mcc, mnc))) return operator;
	}
	return null;
}

export function resolveUssdOperatorEntry(
	operatorName: string | null | undefined,
	mcc?: number | null,
	mnc?: number | null,
): UssdOperatorPresets | null {
	const byMccMnc = resolveUssdOperatorEntryByMccMnc(mcc, mnc);
	if (byMccMnc) return byMccMnc;

	const name = operatorName?.trim().toLowerCase();
	if (!name) return null;

	const keyed = getKeyedOperatorEntries();
	const exact = keyed.find(entry => entry.matchKey === name);
	if (exact) return exact.operator;

	for (const entry of keyed) {
		if (name.includes(entry.matchKey)) return entry.operator;
	}

	return null;
}

export function resolveUssdOperatorPresets(
	operatorName: string | null | undefined,
	mcc?: number | null,
	mnc?: number | null,
): UssdPreset[] {
	return resolveUssdOperatorEntry(operatorName, mcc, mnc)?.presets ?? [];
}

const MAX_NUMBER_COMMANDS = 3;

export function getUssdNumberCommands(
	operatorName: string | null | undefined,
	mcc?: number | null,
	mnc?: number | null,
): string[] {
	const entry = resolveUssdOperatorEntry(operatorName, mcc, mnc);
	if (!entry || entry.skipAutoUssd) return [];
	return entry.presets
		.filter(preset => preset.category === 'number')
		.map(preset => preset.command)
		.slice(0, MAX_NUMBER_COMMANDS);
}

export function getUssdNumberCommand(
	operatorName: string | null | undefined,
	mcc?: number | null,
	mnc?: number | null,
): string | null {
	return getUssdNumberCommands(operatorName, mcc, mnc)[0] ?? null;
}

export function shouldSkipSimNumberResolve(
	operatorName: string | null | undefined,
	mcc?: number | null,
	mnc?: number | null,
): boolean {
	const entry = resolveUssdOperatorEntry(operatorName, mcc, mnc);
	if (!entry) return false;
	return Boolean(entry.skipAutoUssd);
}

/** Primary match key for operator-specific USSD response parsers. */
export function getUssdOperatorParserKey(
	operatorName: string | null | undefined,
	mcc?: number | null,
	mnc?: number | null,
): string | null {
	const entry = resolveUssdOperatorEntry(operatorName, mcc, mnc);
	if (!entry) return null;
	return entry.matchKeys[0] ?? null;
}

const ASYNC_USSD_ACCEPT_PATTERNS = [
	/prinyata/i,
	/qabul qilindi/i,
	/sms tarzida/i,
	/vide sms/i,
	/in\s+messages/i,
];

export function isAsyncUssdAcceptResponse(
	operatorKey: string | null | undefined,
	text: string | null | undefined,
	parsedMsisdn?: string | null,
): boolean {
	if (parsedMsisdn) return false;
	if (!text?.trim()) return false;
	const key = operatorKey?.toLowerCase();
	if (key !== 'beeline' && key !== 'beeline uz' && key !== 'unitel') return false;
	return ASYNC_USSD_ACCEPT_PATTERNS.some(pattern => pattern.test(text));
}

export function isAsyncUssdCommand(
	operatorName: string | null | undefined,
	command: string | null | undefined,
	mcc?: number | null,
	mnc?: number | null,
): boolean {
	if (!command?.trim()) return false;
	const presets = resolveUssdOperatorPresets(operatorName, mcc, mnc);
	return presets.some(preset => preset.command === command.trim() && preset.asyncDelivery === 'sms');
}

export type GroupedUssdPresets = { category: UssdPresetCategory; label: string; presets: UssdPreset[] };

export function groupUssdPresetsByCategory(presets: UssdPreset[]): GroupedUssdPresets[] {
	const buckets = new Map<UssdPresetCategory, UssdPreset[]>();

	for (const preset of presets) {
		const category = preset.category ?? 'other';
		const list = buckets.get(category) ?? [];
		list.push(preset);
		buckets.set(category, list);
	}

	return USSD_PRESET_CATEGORY_ORDER.flatMap(category => {
		const group = buckets.get(category);
		if (!group?.length) return [];
		return [{ category, label: USSD_PRESET_CATEGORY_LABELS[category], presets: group }];
	});
}

/** Parity map for auto-ussd and ingest handlers — first number-category command per match key. */
export const USSD_SIM_COMMANDS: Record<string, string> = Object.fromEntries(
	USSD_OPERATOR_PRESETS.flatMap(operator =>
		operator.skipAutoUssd
			? []
			: operator.matchKeys.flatMap(matchKey => {
					const command = getUssdNumberCommand(matchKey);
					return command ? [[matchKey, command] as const] : [];
				}),
	),
);
